import { FC, Fragment } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { useAppStore } from "@/store";
import { MDSIcon, MDSIconColor, MDSIconKind } from "@/mds";
import type { OnboardingStepperStatus } from "@/store/onboarding";

const iconColor = (status: OnboardingStepperStatus): MDSIconColor =>
  status === "upcoming" ? MDSIconColor.Placeholder : MDSIconColor.Emphasis;

const ICON_SIZE = 16;
const CIRCLE_SIZE = 32;

export const OnboardingStepper: FC = observer(() => {
  const { store } = useAppStore();
  const steps = store.onboarding.stepperSteps;

  const hasMeetingAndVoice =
    steps.some(s => s.id === "meeting-permissions") &&
    steps.some(s => s.id === "voice-permissions");

  if (!hasMeetingAndVoice) return null;

  const allCompleted = steps.every(s => s.status === "completed");
  const checkStatus: OnboardingStepperStatus = allCompleted ? "active" : "upcoming";

  return (
    <StyledStepperContainer>
      {steps.map((step, i) => (
        <Fragment key={step.id}>
          {i > 0 && <StyledStepConnector />}
          <StyledStepCircle status={step.status}>
            <MDSIcon kind={step.icon} size={ICON_SIZE} color={iconColor(step.status)} />
          </StyledStepCircle>
        </Fragment>
      ))}
      <StyledStepConnector />
      <StyledStepCircle status={checkStatus}>
        <MDSIcon kind={MDSIconKind.Check} size={ICON_SIZE} color={iconColor(checkStatus)} />
      </StyledStepCircle>
    </StyledStepperContainer>
  );
});

const StyledStepperContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-right: auto;
`;

const StyledStepCircle = styled.div<{ status: OnboardingStepperStatus }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${CIRCLE_SIZE}px;
  height: ${CIRCLE_SIZE}px;
  border-radius: 100px;
  flex-shrink: 0;
  background: ${({ theme, status }) =>
    status === "upcoming"
      ? theme.colors.background.button.default.default
      : theme.colors.background.button.toggle.toggled};
`;

const StyledStepConnector = styled.div`
  width: 24px;
  height: 2px;
  flex-shrink: 0;
  border-radius: 1px;
  background: ${({ theme }) => theme.colors.border.default};
`;
